import { useMemo } from 'react';

import { Box, Skeleton, Stack } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { GoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useSearchParams } from 'react-router';

import GoLogo from '@/components/GoLogo';
import Loader from '@/components/Loader';
import Typography from '@/components/Typography';
import { spacing } from '@/constants/ui';
import { OnboardingLayout } from '@/layouts/OnboardingLayout';
import ErrorMessage from '@/pages/Onboarding/components/ErrorMessage';
import { ThanksMessage } from '@/pages/Onboarding/components/ThanksMessage';
import { getDomainConfig } from '@/pages/Onboarding/OnboardingForm/getDomainConfig';
import useOnboardingForm from '@/pages/Onboarding/OnboardingForm/useOnboardingForm';

export const VERIFICATION_TOKEN_PARAM = 'verificationToken';
const OnboardingForm = () => {
  const [urlSearchParams] = useSearchParams();
  const verificationToken = useMemo(
    () => urlSearchParams.get(VERIFICATION_TOKEN_PARAM) ?? '',
    [urlSearchParams]
  );

  const {
    data: domainConfig,
    isError: isDomainConfigError,
    isLoading: isDomainConfigLoading
  } = useQuery({
    queryKey: [verificationToken],
    queryFn: () => getDomainConfig(verificationToken),
    gcTime: Infinity,
    enabled: !!verificationToken
  });

  const { activeStep, steps, refreshReCaptcha, loading, failed, onVerify } = useOnboardingForm(
    domainConfig,
    verificationToken
  );

  return (
    <OnboardingLayout>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%'
        }}
      >
        {failed && <ThanksMessage />}
        {loading && (
          <Box>
            <Typography
              size={24}
              sx={{ fontWeight: 'bold', mb: 1.5 }}
            >
              You're all set!
            </Typography>
            <Typography
              size={24}
              sx={{ fontWeight: 'bold', mb: 1.5 }}
            >
              You're now ready to (GX) go!
            </Typography>
            <Loader />
          </Box>
        )}
        {!loading && !failed && (
          <Box>
            <GoLogo open />
            <Box sx={{ maxWidth: 650, mt: spacing['3'] }}>
              {isDomainConfigError && <ErrorMessage />}
              {!isDomainConfigError && (
                <>
                  <GoogleReCaptcha
                    action='onboardingForm'
                    refreshReCaptcha={refreshReCaptcha}
                    onVerify={onVerify}
                  />
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      sx={{
                        mb: 0.5,
                        color: 'info.dark',
                        fontSize: 12
                      }}
                    >{`Step ${activeStep + 1} of ${steps.length}`}</Typography>
                    <Stack
                      direction='row'
                      spacing={1}
                      sx={{ width: '100%' }}
                    >
                      {steps.map(({ name }, index) => {
                        return (
                          <Box
                            key={name}
                            sx={{
                              height: {
                                xs: 2,
                                md: 4
                              },
                              width: `${100 / steps.length}%`,
                              backgroundColor: activeStep >= index ? 'primary.main' : 'info.dark'
                            }}
                          />
                        );
                      })}
                    </Stack>
                  </Box>
                  {isDomainConfigLoading && (
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      {[1, 2, 3].map(key => {
                        return (
                          <Skeleton
                            key={key}
                            height='54px'
                            sx={{ mb: 2, borderRadius: 0, WebkitTransform: 'scale(1, 1)' }}
                            width='220px'
                          />
                        );
                      })}
                    </Box>
                  )}
                  {!isDomainConfigLoading && steps[activeStep]?.component}
                </>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </OnboardingLayout>
  );
};

export default OnboardingForm;
